import React, { useState, useEffect } from 'react';

interface PhotoSchedule {
  scheduledDate: string;
  description: string;
  status: 'pending' | 'assigned' | 'completed' | 'overdue';
  assignedWorker?: {
    _id: string;
    name: string;
    email: string;
    region: string;
  };
  photoFilename?: string;
  reminderSentDate?: string;
}

interface PhotoPackage {
  _id: string;
  originalOrder: {
    _id: string;
    treesOrdered: number;
    status: string;
    customerName: string;
  };
  customer: {
    _id: string;
    name: string;
    email: string;
  };
  customerEmail: string;
  packageType: 'first-year' | 'three-year' | 'lifetime';
  packageName?: string;
  totalPhotoCount: number;
  photoSchedule: PhotoSchedule[];
  status: 'active' | 'completed' | 'cancelled';
  purchaseDate: string;
  createdAt: string;
}

interface PhotoPackagesTabProps {
  onAssignWorker: (packageId: string, scheduleIndex: number) => void;
  onViewDetails: (photoPackage: PhotoPackage) => void;
}

export const PhotoPackagesTab: React.FC<PhotoPackagesTabProps> = ({
  onAssignWorker,
  onViewDetails
}) => {
  const [photoPackages, setPhotoPackages] = useState<PhotoPackage[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<any>(null);
  const [filterStatus, setFilterStatus] = useState<string>('all');

  useEffect(() => {
    loadPhotoPackages();
    loadStats();
  }, []);

  const loadPhotoPackages = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch('/api/admin/photo-packages', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setPhotoPackages(data.photoPackages);
      }
    } catch (error) {
      console.error('Load photo packages error:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch('/api/admin/photo-packages-stats', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Load stats error:', error);
    }
  };

  const getPackageName = (type: string) => {
    const names = {
      'first-year': 'First Year Journey',
      'three-year': 'Three Year Story',
      'lifetime': 'Lifetime Growth'
    };
    return names[type as keyof typeof names] || type;
  };

  const getStatusColor = (status: string) => {
    const colors = {
      'pending': '#ffc107',
      'assigned': '#17a2b8',
      'completed': '#28a745',
      'overdue': '#dc3545'
    };
    return colors[status as keyof typeof colors] || '#6c757d';
  };

  const getUpcomingPhoto = (schedule: PhotoSchedule[]) => {
    return schedule.find(s => s.status === 'pending' || s.status === 'assigned' || s.status === 'overdue');
  };

  const filteredPackages = filterStatus === 'all'
    ? photoPackages
    : photoPackages.filter(p => p.status === filterStatus);

  if (loading) {
    return (
      <div style={{
        textAlign: 'center',
        padding: '3rem',
        color: '#666'
      }}>
        Loading photo packages...
      </div>
    );
  }

  return (
    <div style={{
      background: 'white',
      borderRadius: '0 0 15px 15px',
      padding: '2rem',
      boxShadow: '0 5px 15px rgba(0,0,0,0.1)',
      minHeight: '500px'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '2rem'
      }}>
        <h2 style={{ color: '#228B22', margin: 0 }}>Photo Package Management</h2>
        <span style={{ color: '#666', fontSize: '0.9rem' }}>
          {photoPackages.length} packages
        </span>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1rem',
          marginBottom: '2rem'
        }}>
          <div style={{
            background: '#e3f2fd',
            padding: '1rem',
            borderRadius: '10px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#1976d2' }}>
              {stats.totalPackages}
            </div>
            <div style={{ color: '#666', fontSize: '0.9rem' }}>Total Packages</div>
          </div>
          <div style={{
            background: '#e8f5e9',
            padding: '1rem',
            borderRadius: '10px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#388e3c' }}>
              {stats.activePackages}
            </div>
            <div style={{ color: '#666', fontSize: '0.9rem' }}>Active</div>
          </div>
          <div style={{
            background: '#fff3e0',
            padding: '1rem',
            borderRadius: '10px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#f57c00' }}>
              {stats.overduePhotos}
            </div>
            <div style={{ color: '#666', fontSize: '0.9rem' }}>Overdue Photos</div>
          </div>
          <div style={{
            background: '#f3e5f5',
            padding: '1rem',
            borderRadius: '10px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#7b1fa2' }}>
              ${stats.totalRevenue}
            </div>
            <div style={{ color: '#666', fontSize: '0.9rem' }}>Revenue</div>
          </div>
        </div>
      )}

      {/* Filter Buttons */}
      <div style={{
        display: 'flex',
        gap: '0.5rem',
        marginBottom: '1.5rem'
      }}>
        <button
          onClick={() => setFilterStatus('all')}
          style={{
            padding: '0.5rem 1rem',
            background: filterStatus === 'all' ? '#228B22' : '#e9ecef',
            color: filterStatus === 'all' ? 'white' : '#666',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '0.9rem'
          }}
        >
          All
        </button>
        <button
          onClick={() => setFilterStatus('active')}
          style={{
            padding: '0.5rem 1rem',
            background: filterStatus === 'active' ? '#228B22' : '#e9ecef',
            color: filterStatus === 'active' ? 'white' : '#666',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '0.9rem'
          }}
        >
          Active
        </button>
        <button
          onClick={() => setFilterStatus('completed')}
          style={{
            padding: '0.5rem 1rem',
            background: filterStatus === 'completed' ? '#228B22' : '#e9ecef',
            color: filterStatus === 'completed' ? 'white' : '#666',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '0.9rem'
          }}
        >
          Completed
        </button>
      </div>

      {/* Photo Packages List */}
      {filteredPackages.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '3rem',
          color: '#666'
        }}>
          <p>No photo packages found.</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '1rem' }}>
          {filteredPackages.map(pkg => {
            const upcomingPhoto = getUpcomingPhoto(pkg.photoSchedule);
            const completedCount = pkg.photoSchedule.filter(s => s.status === 'completed').length;

            return (
              <div
                key={pkg._id}
                style={{
                  background: '#f8f9fa',
                  border: '1px solid #e9ecef',
                  borderRadius: '12px',
                  padding: '1.5rem',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start'
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    marginBottom: '0.75rem'
                  }}>
                    <div>
                      <h4 style={{
                        margin: 0,
                        color: '#228B22',
                        fontSize: '1.1rem'
                      }}>
                        {getPackageName(pkg.packageType)}
                      </h4>
                      <div style={{
                        fontSize: '0.85rem',
                        color: '#666',
                        marginTop: '0.25rem'
                      }}>
                        {pkg.customerEmail} • Order #{pkg.originalOrder._id.slice(-6)}
                      </div>
                    </div>
                    <span style={{
                      background: pkg.status === 'active' ? '#d1ecf1' : pkg.status === 'completed' ? '#d4edda' : '#f8d7da',
                      color: pkg.status === 'active' ? '#0c5460' : pkg.status === 'completed' ? '#155724' : '#721c24',
                      padding: '0.25rem 0.75rem',
                      borderRadius: '20px',
                      fontSize: '0.8rem',
                      fontWeight: '600',
                      textTransform: 'uppercase'
                    }}>
                      {pkg.status}
                    </span>
                  </div>

                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                    gap: '1rem',
                    color: '#666',
                    fontSize: '0.9rem',
                    marginBottom: '0.75rem'
                  }}>
                    <div>📸 {completedCount}/{pkg.totalPhotoCount} photos completed</div>
                    <div>📅 Purchased: {new Date(pkg.purchaseDate).toLocaleDateString()}</div>
                  </div>

                  {/* Next scheduled photo */}
                  {upcomingPhoto && (
                    <div style={{
                      background: 'white',
                      padding: '0.75rem',
                      borderRadius: '8px',
                      fontSize: '0.85rem',
                      marginTop: '0.5rem'
                    }}>
                      <div style={{
                        fontWeight: '600',
                        marginBottom: '0.25rem',
                        color: getStatusColor(upcomingPhoto.status)
                      }}>
                        Next Update: {upcomingPhoto.description}
                      </div>
                      <div style={{ color: '#666' }}>
                        📅 {new Date(upcomingPhoto.scheduledDate).toLocaleDateString()}
                        {upcomingPhoto.assignedWorker && (
                          <span> • 👷 {upcomingPhoto.assignedWorker.name}</span>
                        )}
                        {upcomingPhoto.status === 'overdue' && (
                          <span style={{ color: '#dc3545', marginLeft: '0.5rem', fontWeight: '600' }}>
                            ⚠️ OVERDUE
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', gap: '0.5rem', flexDirection: 'column', marginLeft: '1rem' }}>
                  <button
                    onClick={() => onViewDetails(pkg)}
                    style={{
                      padding: '0.5rem 1rem',
                      background: '#0d6efd',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    View Details
                  </button>
                  {upcomingPhoto && !upcomingPhoto.assignedWorker && (
                    <button
                      onClick={() => {
                        const scheduleIndex = pkg.photoSchedule.findIndex(s => s === upcomingPhoto);
                        onAssignWorker(pkg._id, scheduleIndex);
                      }}
                      style={{
                        padding: '0.5rem 1rem',
                        background: '#ffc107',
                        color: '#000',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.9rem',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      Assign Worker
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
